import React from 'react';
import { X, ArrowRight } from 'lucide-react';

/**
 * FAQCard - Modal-style FAQ card with dark answer box
 * Matches the design: Q icon, dark navy answer, footer text
 */
export const FAQCard = ({ 
  question, 
  answer, 
  onClose,
  relatedQuestions = [],
  onSelectQuestion,
  showRelated = true,
  isModal = false
}) => {
  // Function to highlight key phrases in bold
  const formatAnswer = (text) => {
    if (!text) return '';
    
    // Common business terms to highlight
    const highlightPatterns = [
      /\b(strategic move|strategic decision)\b/gi,
      /\b(\d+%\s*(?:corporate rate|tax rate|rate|fee))\b/gi,
      /\b(limited liability|liability protection)\b/gi,
      /\b(pass-through taxation)\b/gi,
      /\b(no state income tax)\b/gi,
      /\b(privacy protection)\b/gi,
      /\b(business-friendly)\b/gi,
      /\b(within \d+ (?:days|hours|weeks|business days))\b/gi,
      /\b(required|mandatory|must)\b/gi,
      /\$[\d,]+(?:\.\d{2})?/g,
    ];
    
    let formattedText = text;
    highlightPatterns.forEach(pattern => {
      formattedText = formattedText.replace(pattern, '<strong>$&</strong>');
    });
    
    return formattedText;
  };

  const cardContent = (
    <>
      {/* Close button for modal mode */}
      {isModal && onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="faq-card-close"
        >
          <X size={24} />
        </button>
      )}
      
      {/* Question Section */}
      <div className="flex items-start gap-4 mb-6" data-testid="faq-card-question">
        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
          <span className="text-gray-600 font-semibold text-lg">Q</span>
        </div>
        <p className="text-gray-800 text-lg leading-relaxed pt-2">
          {question}
        </p>
      </div>
      
      {/* Answer Card - Dark Navy */}
      <div 
        className="bg-[#1a2332] rounded-xl p-6 mb-4"
        data-testid="faq-card-answer"
      >
        <p 
          className="text-white leading-relaxed"
          dangerouslySetInnerHTML={{ __html: formatAnswer(answer) }}
        />
        
        {/* Divider */}
        <div className="border-t border-gray-600 mt-6 pt-4">
          <p className="text-gray-400 text-sm italic">
            Frequently asked by founders. Expert Answered.
          </p>
        </div>
      </div>
      
      {/* More Questions Section */}
      {showRelated && relatedQuestions.length > 0 && (
        <div className="mt-8" data-testid="faq-related-questions">
          <h3 className="text-gray-800 font-medium mb-4">
            More <span className="text-amber-500">Questions</span>?
          </h3>
          
          <div className="space-y-2">
            {relatedQuestions.map((faq, index) => (
              <button
                key={faq.id || index}
                onClick={() => onSelectQuestion && onSelectQuestion(faq)}
                className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors text-left group"
                data-testid={`faq-related-item-${index}`}
              >
                <span className="text-gray-700 pr-4">{faq.question}</span>
                <ArrowRight className="text-amber-500 flex-shrink-0 group-hover:translate-x-1 transition-transform" size={18} />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );

  if (isModal) {
    return (
      <div 
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
        data-testid="faq-card-modal"
      >
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative shadow-2xl">
          {cardContent}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-white rounded-2xl p-8 shadow-lg"
      data-testid="faq-card"
    >
      {cardContent}
    </div>
  );
};

export default FAQCard;
